import React from 'react';
import './Terms.scss';
import {NavLink} from "react-router-dom";
import {LEGAL} from "../../constants/legal";

const {
    companyName, serviceName, website, contactEmail, jurisdiction, minimumAge, lastUpdated, priceChangeNoticeDays
} = LEGAL;

const Terms = () => {
    return (
        <div className="Terms">
            <article className="Terms__content">
                <header>
                    <h1>{serviceName} Terms of Service</h1>
                    <p className="Terms__updated">Last updated: {lastUpdated}</p>
                </header>

                <p className="Terms__highlight">
                    <strong>Important:</strong> {serviceName} creates <strong>AI simulations</strong> based on chat
                    history and voice samples you upload. You are always talking to an artificial intelligence, never
                    to the real person. Replies, including voice messages, are generated automatically and may be
                    inaccurate, inappropriate or nothing like what the real person would say.
                </p>

                <section>
                    <h2>1. Acceptance of these Terms</h2>
                    <p>
                        These Terms of Service ("Terms") are an agreement between you and {companyName} ("we", "us"),
                        the operator of {serviceName} at {website} (the "Service"). By creating an account or using the
                        Service, you agree to these Terms. If you do not agree, do not use the Service.
                    </p>
                </section>

                <section>
                    <h2>2. Eligibility</h2>
                    <p>
                        You must be at least {minimumAge} years old to use the Service. By using it, you confirm that
                        you meet this requirement and are able to enter into a binding agreement.
                    </p>
                </section>

                <section>
                    <h2>3. What the Service is, and what it is not</h2>
                    <ul>
                        <li>The Service uses artificial intelligence to generate text and voice replies that imitate
                            the writing style and voice found in the material you upload.
                        </li>
                        <li>An AI simulation is <strong>not</strong> the real person. It has no consciousness, feelings
                            or memories of its own, and it does not speak for the real person.
                        </li>
                        <li>Generated content can be wrong, made up, or emotionally upsetting. Do not rely on it for
                            facts, decisions, or medical, legal, financial or other professional advice.
                        </li>
                        <li>The Service is not therapy or grief counselling and is not a substitute for professional
                            help. If you are in crisis or thinking about harming yourself, contact your local emergency
                            number or a crisis helpline right away.
                        </li>
                    </ul>
                </section>

                <section>
                    <h2>4. Your content and the consent of others</h2>
                    <p>
                        "Your Content" means the chat exports, voice samples, images and messages you upload or send.
                        Your Content often includes personal data of other people. By uploading it, you confirm that:
                    </p>
                    <ul>
                        <li>you were a participant in the uploaded conversation, or you otherwise have the right to use
                            it;
                        </li>
                        <li>the person being simulated has given their consent, <strong>or</strong> that person has
                            died and you are a close family member or are otherwise legally entitled to use their
                            personal data and likeness;
                        </li>
                        <li>you have the right to use any voice sample and image you upload for this purpose;</li>
                        <li>your use does not break any law or the rights of any person, including privacy, data
                            protection, publicity and intellectual property rights.
                        </li>
                    </ul>
                    <p>
                        You are solely responsible for Your Content. We may ask for proof of consent and may remove
                        any simulation if we receive a credible complaint from the person depicted or their
                        representatives.
                    </p>
                </section>

                <section>
                    <h2>5. Prohibited uses</h2>
                    <p>You must not use the Service to:</p>
                    <ul>
                        <li>impersonate a real person to anyone else, or pass off generated text or voice as genuinely
                            coming from that person;
                        </li>
                        <li>commit fraud or scams, or get money, information or access by deception;</li>
                        <li>harass, stalk, threaten, defame or intimidate anyone;</li>
                        <li>create a simulation of a living person who has not consented, including former partners,
                            public figures or celebrities;
                        </li>
                        <li>create a simulation of a minor, or any sexual content involving a real person;</li>
                        <li>record, share or publish generated voice messages in a way that may mislead others;</li>
                        <li>reverse engineer, scrape or overload the Service, or get around usage limits.</li>
                    </ul>
                    <p>We may suspend or delete accounts that break these rules, without refund.</p>
                </section>

                <section>
                    <h2>6. Voice simulation</h2>
                    <p>
                        Voice replies are synthetic audio generated by AI. They are provided for your private,
                        personal use within the Service only. You must not use generated audio outside the Service in
                        any way that could make someone believe it is a real recording of the person.
                    </p>
                </section>

                <section>
                    <h2>7. Privacy and your data</h2>
                    <ul>
                        <li>We process Your Content only to provide the Service to you. We do not sell it.</li>
                        <li>We do not use Your Content to train AI models.</li>
                        <li>Your Content may be processed by third-party providers we use to run the Service (for
                            example, hosting, AI text and voice generation, and payments).
                        </li>
                        <li>You can delete a chat, or your whole account, at any time. Deleting removes the uploaded
                            data and generated messages from our systems right away, except where the law requires us
                            to keep it.
                        </li>
                        <li>More details are in our <NavLink to={'/privacy'}>Privacy Policy</NavLink>.</li>
                    </ul>
                </section>

                <section>
                    <h2>8. Subscriptions and payments</h2>
                    <ul>
                        <li>Some features require a paid subscription. Prices and plan limits are shown before you
                            buy.
                        </li>
                        <li>Payments are handled by our payment provider, Stripe. We do not store your full card
                            details.
                        </li>
                        <li>Subscriptions renew automatically at the end of each billing period until you cancel. You
                            can cancel at any time from your account settings. Cancellation takes effect at the end of
                            the current billing period.
                        </li>
                        <li>Except where the law requires otherwise, payments are not refundable. [Adjust to your
                            refund policy and local consumer law.]
                        </li>
                        <li>We may change prices with at least {priceChangeNoticeDays} days' notice. The new price applies from your next
                            billing period.
                        </li>
                    </ul>
                </section>

                <section>
                    <h2>9. Intellectual property</h2>
                    <p>
                        You keep the rights you have in Your Content. You give us a limited licence to store, process
                        and use it only to provide the Service to you. The Service itself, including its software and
                        design, belongs to {companyName}.
                    </p>
                </section>

                <section>
                    <h2>10. Termination</h2>
                    <p>
                        You can stop using the Service and delete your account at any time. We may suspend or end your
                        access if you break these Terms or if required by law.
                    </p>
                </section>

                <section>
                    <h2>11. Disclaimers</h2>
                    <p>
                        The Service is provided "as is" and "as available". To the extent allowed by law, we do not
                        promise that it will be uninterrupted, error-free, or that generated content will be accurate,
                        suitable or resemble the real person.
                    </p>
                </section>

                <section>
                    <h2>12. Limitation of liability</h2>
                    <p>
                        To the extent allowed by law, {companyName} is not liable for indirect, incidental or
                        consequential damages, or for emotional distress caused by generated content. Our total
                        liability is limited to the amount you paid us in the 12 months before the claim.
                    </p>
                </section>

                <section>
                    <h2>13. Indemnification</h2>
                    <p>
                        You agree to cover any claims, losses or costs brought against us by third parties because of
                        Your Content or your breach of these Terms, including claims by a person you simulated.
                    </p>
                </section>

                <section>
                    <h2>14. Changes to these Terms</h2>
                    <p>
                        We may update these Terms. If the changes are significant, we will notify you in the Service
                        or by email before they take effect. If you keep using the Service after that, you accept the
                        updated Terms.
                    </p>
                </section>

                <section>
                    <h2>15. Governing law</h2>
                    <p>
                        These Terms are governed by the laws of {jurisdiction}, without affecting any mandatory
                        consumer protection rights you have where you live.
                    </p>
                </section>

                <section>
                    <h2>16. Contact</h2>
                    <p>
                        Questions, consent complaints or removal requests: {contactEmail}
                    </p>
                </section>

                <NavLink to={'/'}>Back to {serviceName}</NavLink>
            </article>
        </div>
    );
};

export default Terms;
